import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAreas, useTemasByArea, usePreguntasByTema } from '../hooks/useDataService';
import { percentageToIcne, getNivelIcne } from '../constants/exaniConstants';
import MathGraph from './MathGraph';
import { QuestionText, OptionText, isImageDataOrUrl } from './MathRenderer';
import '../styles/Simulador.css';
import '../styles/MathGraph.css';

const SimuladorExamen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estado del simulador
  const [step, setStep] = useState('config'); // config, running, results, review
  const [config, setConfig] = useState({
    areas: [],
    questionCount: 50,
    timeLimit: 180, // 3 horas en minutos
    difficulty: 'mixed'
  });
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { areas: allAreas } = useAreas();
  const [selectedAreaTemas, setSelectedAreaTemas] = useState({});
  const [allSelectedQuestions, setAllSelectedQuestions] = useState([]);
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const questionStartTimes = useRef({});

  // Cargar temas para áreas seleccionadas
  useEffect(() => {
    if (config.areas.length === 0) return;
    
    const loadTemas = async () => {
      const { dataService } = await import('../services/dataService');
      const temasMap = {};
      
      for (const areaId of config.areas) {
        const { temas } = await dataService.getTemasByArea(areaId);
        if (temas && temas.length > 0) {
          temasMap[areaId] = temas;
        }
      }
      setSelectedAreaTemas(temasMap);
    };
    
    loadTemas();
  }, [config.areas]);

  // Timer
  useEffect(() => {
    if (!timerActive || step !== 'running') return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [timerActive, step]);

  const handleTimeUp = useCallback(() => {
    setTimerActive(false);
    finishSimulador();
  }, []);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startSimulador = useCallback(async () => {
    if (config.areas.length === 0) {
      setError('Selecciona al menos un área');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Obtener preguntas de todas las áreas seleccionadas
      const { dataService } = await import('../services/dataService');
      let allQuestions = [];
      
      for (const areaId of config.areas) {
        const { temas } = await dataService.getTemasByArea(areaId);
        if (!temas || temas.length === 0) continue;
        
        // Obtener preguntas de cada tema
        for (const tema of temas) {
          const { preguntas } = await dataService.getPreguntasByTema(tema.id);
          if (preguntas && preguntas.length > 0) {
            const area = allAreas.find(a => a.id === areaId);
            const tagged = preguntas.map(q => ({
              ...q,
              areaId: areaId,
              temaId: tema.id,
              temaNombre: tema.nombre,
              areaNombre: area?.nombre || `Área ${areaId}`,
              // Las áreas diagnósticas (p. ej. Inglés B1) no suman al puntaje global Ceneval.
              puntua: area?.puntua !== false && area?.tipo !== 'diagnostica'
            }));
            allQuestions = [...allQuestions, ...tagged];
          }
        }
      }
      
      if (allQuestions.length === 0) {
        setError('No hay preguntas disponibles para las áreas seleccionadas');
        setLoading(false);
        return;
      }
      
      // Mezclar y tomar la cantidad configurada
      const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, config.questionCount);
      
      setQuestions(selected);
      setAllSelectedQuestions(selected);
      setTimeLeft(config.timeLimit * 60);
      setCurrentIndex(0);
      setAnswers({});
      setScore(0);
      setResults(null);
      setReviewMode(false);
      startTimeRef.current = Date.now();
      questionStartTimes.current = {};
      questionStartTimes.current[0] = Date.now();
      
      setStep('running');
      setTimerActive(true);
      setLoading(false);
    } catch (err) {
      console.error('Error iniciando simulador:', err);
      setError('Error al cargar preguntas: ' + err.message);
      setLoading(false);
    }
  }, [config, allAreas]);

  const finishSimulador = useCallback(() => {
    const scoredTotal = questions.filter(q => q.puntua !== false).length || questions.length;
    let correct = 0;
    const detailedResults = questions.map((q, i) => {
      const userAnswer = answers[i];
      const correctAnswer = q.correcta ?? q.correctAnswer ?? q.correctIndex ?? 0;
      const isCorrect = userAnswer === correctAnswer;
      if (isCorrect && q.puntua !== false) correct++;
      return {
        questionId: q.id,
        question: q.pregunta || q.question || q.text,
        areaId: q.areaId,
        areaNombre: q.areaNombre,
        temaId: q.temaId,
        temaNombre: q.temaNombre,
        userAnswer,
        correctAnswer,
        isCorrect,
        timeSpent: questionStartTimes.current[i] 
          ? Math.floor((Date.now() - questionStartTimes.current[i]) / 1000)
          : 0,
        explicacion: q.explicacion
      };
    });
    
    const percentage = Math.round((correct / scoredTotal) * 100);
    const scoreIcne = percentageToIcne(percentage);
    const nivelIcne = getNivelIcne(scoreIcne);
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    const resultData = {
      score: correct,
      total: scoredTotal,
      percentage,
      scoreIcne,
      nivelIcne,
      timeSpent,
      config: { ...config },
      details: detailedResults,
      timestamp: Date.now()
    };
    
    setResults(resultData);
    setScore(correct);
    setStep('results');
    setTimerActive(false);
    
    // Guardar resultado si hay usuario
    if (user) {
      import('../services/dataService').then(({ default: dataService }) => {
        dataService.saveQuizResult(user.uid, {
          ...resultData,
          tipo: 'simulador',
          simuladorConfig: config
        }).catch(console.error);
      });
    }
  }, [questions, answers, config, user]);

  const handleAnswer = useCallback((questionIndex, answer) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: answer }));
    // Registrar tiempo de inicio de la siguiente pregunta
    if (questionIndex + 1 < questions.length) {
      questionStartTimes.current[questionIndex + 1] = Date.now();
    }
  }, [questions.length]);

  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, questions.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleRetry = useCallback(() => {
    setStep('config');
    setCurrentIndex(0);
    setAnswers({});
    setQuestions([]);
    setScore(0);
    setResults(null);
    setReviewMode(false);
    setError(null);
  }, []);

  const handleReview = useCallback(() => {
    setReviewMode(true);
    setStep('review');
  }, []);

  const getCorrectAnswer = (q) => q.correcta ?? q.correctAnswer ?? q.correctIndex ?? 0;
  const getRespuestas = (q) => {
    if (q.respuestas && typeof q.respuestas === 'object') {
      return Object.values(q.respuestas).map(r => {
        if (typeof r === 'string') return r;
        return r.respuesta || r.texto || r.imagen || r.url || r;
      });
    }
    if (q.opciones && Array.isArray(q.opciones)) {
      return q.opciones.map(r => {
        if (typeof r === 'string') return r;
        return r.respuesta || r.texto || r.imagen || r.url || r;
      });
    }
    return [];
  };

  const currentQuestion = questions[currentIndex];
  const respuestas = currentQuestion ? getRespuestas(currentQuestion) : [];
  const correctAnswer = currentQuestion ? getCorrectAnswer(currentQuestion) : 0;
  const userAnswer = currentQuestion ? answers[currentIndex] : undefined;
  const isAnswered = userAnswer !== undefined;
  const isCorrect = isAnswered && userAnswer === correctAnswer;

  // Pantalla de configuración
  if (step === 'config') {
    return (
      <div className="simulador-container">
        <div className="simulador-header">
          <h1>Configurar Simulador EXANI-II</h1>
          <p>Selecciona las áreas y parámetros para tu examen de práctica</p>
        </div>
        
        <div className="simulador-config">
          <section className="config-section">
            <h3>Áreas de Conocimiento</h3>
            <div className="areas-grid">
              {allAreas.map(area => (
                <label key={area.id} className={`area-checkbox ${config.areas.includes(area.id) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={config.areas.includes(area.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setConfig(prev => ({ ...prev, areas: [...prev.areas, area.id] }));
                      } else {
                        setConfig(prev => ({ ...prev, areas: prev.areas.filter(a => a !== area.id) }));
                      }
                    }}
                  />
                  <span className="area-icon">{getAreaIcon(area.id)}</span>
                  <span className="area-name">{area.nombre}</span>
                </label>
              ))}
            </div>
          </section>
          
          <section className="config-section">
            <h3>Parámetros del Examen</h3>
            <div className="config-options">
              <div className="config-option">
                <label>Número de preguntas</label>
                <select 
                  value={config.questionCount} 
                  onChange={(e) => setConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
                >
                  <option value={30}>30 (Rápido)</option>
                  <option value={50}>50 (Estándar)</option>
                  <option value={70}>70 (Extendido)</option>
                  <option value={100}>100 (Completo)</option>
                  <option value={138}>138 (Oficial EXANI-II)</option>
                </select>
              </div>
              
              <div className="config-option">
                <label>Tiempo límite (minutos)</label>
                <select 
                  value={config.timeLimit} 
                  onChange={(e) => setConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                >
                  <option value={60}>1 hora</option>
                  <option value={90}>1.5 horas</option>
                  <option value={120}>2 horas</option>
                  <option value={180}>3 horas (Oficial)</option>
                  <option value={240}>4 horas</option>
                  <option value={0}>Sin límite</option>
                </select>
              </div>
              
              <div className="config-option">
                <label>Dificultad</label>
                <select 
                  value={config.difficulty} 
                  onChange={(e) => setConfig(prev => ({ ...prev, difficulty: e.target.value }))}
                >
                  <option value="mixed">Mixta (Oficial)</option>
                  <option value="easy">Fácil</option>
                  <option value="medium">Media</option>
                  <option value="hard">Difícil</option>
                </select>
              </div>
            </div>
          </section>
          
          <section className="config-section">
            <h3>Resumen</h3>
            <div className="config-summary">
              <p>Áreas seleccionadas: <strong>{config.areas.length}</strong></p>
              <p>Preguntas: <strong>{config.questionCount}</strong></p>
              <p>Tiempo: <strong>{config.timeLimit === 0 ? 'Sin límite' : config.timeLimit + ' min'}</strong></p>
              <p>Dificultad: <strong>{config.difficulty}</strong></p>
            </div>
          </section>
          
          <div className="config-actions">
            <button 
              onClick={startSimulador} 
              disabled={config.areas.length === 0 || loading}
              className="btn-primary btn-large"
            >
              {loading ? 'Cargando...' : 'Iniciar Simulador'}
            </button>
            
            {error && <div className="config-error">{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de examen en ejecución
  if (step === 'running') {
    return (
      <div className="simulador-container">
        {/* Header del simulador */}
        <header className="simulador-header-running">
          <div className="simulador-info">
            <span className="area-badge">{currentQuestion?.areaNombre || 'EXANI-II'}</span>
            <span className="tema-badge">{currentQuestion?.temaNombre || ''}</span>
          </div>
          
          <div className="simulador-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
            <span>{currentIndex + 1} / {questions.length}</span>
          </div>
          
          <div className="simulador-timer" style={{ 
            color: timeLeft < 300 ? '#f44336' : timeLeft < 600 ? '#ff9800' : 'var(--text-primary)',
            fontSize: timeLeft < 300 ? '1.5rem' : '1.25rem'
          }}>
            {formatTime(timeLeft)}
          </div>
        </header>
        
        <main className="simulador-main">
          <div className="simulador-card">
            <div className="question-header">
              <span>Pregunta {currentIndex + 1} de {questions.length}</span>
            </div>
            
            <div className="question-text">
              <QuestionText text={currentQuestion?.pregunta || currentQuestion?.question || currentQuestion?.text || 'Cargando...'} />
            </div>

            {currentQuestion?.grafica && (
              <div className="simulador-question-graph" style={{ margin: '1rem 0' }}>
                <MathGraph config={currentQuestion.grafica} />
              </div>
            )}
            
            <div className="answers-grid">
              {respuestas.map((respuesta, idx) => {
                const hasImg = isImageDataOrUrl(respuesta);
                return (
                  <button
                    key={idx}
                    className={`answer-option ${isAnswered && userAnswer === idx ? 'selected' : ''} ${hasImg ? 'has-image' : ''}`}
                    onClick={() => handleAnswer(currentIndex, idx)}
                    disabled={!timerActive}
                  >
                    <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                    <span className="option-text">
                      <OptionText text={respuesta} />
                    </span>
                  </button>
                );
              })}
            </div>
            
            <div className="simulador-navigation">
              <button 
                onClick={handlePrevious} 
                disabled={currentIndex === 0}
                className="btn-secondary"
              >
                ← Anterior
              </button>
              
              <div className="question-nav-dots">
                {questions.map((_, idx) => (
                  <button
                    key={idx}
                    className={`nav-dot ${idx === currentIndex ? 'active' : ''} ${answers[idx] !== undefined ? 'answered' : ''}`}
                    onClick={() => setCurrentIndex(idx)}
                    disabled={!timerActive}
                    title={`Pregunta ${idx + 1}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={currentIndex === questions.length - 1 ? finishSimulador : handleNext} 
                disabled={!timerActive}
                className={`btn-primary ${currentIndex === questions.length - 1 ? 'finish-btn' : ''}`}
              >
                {currentIndex === questions.length - 1 ? 'Finalizar Examen' : 'Siguiente →'}
              </button>
            </div>
          </div>
          
          {/* Panel lateral de navegación rápida en desktop */}
          <aside className="simulador-sidebar">
            <h4>Navegación Rápida</h4>
            <div className="sidebar-dots">
              {questions.map((_, idx) => (
                <button
                  key={idx}
                  className={`sidebar-dot ${idx === currentIndex ? 'active' : ''} ${answers[idx] !== undefined ? 'answered' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                  disabled={!timerActive}
                  title={`P${idx + 1}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </aside>
        </main>
      </div>
    );
  }

  // Pantalla de resultados
  if (step === 'results' || step === 'review') {
    const percentage = results?.percentage || 0;
    const passed = percentage >= 60; // 60% para pasar
    
    return (
      <div className="simulador-container">
        <header className="simulador-header-results">
          <h1>Resultados del Simulador</h1>
          <div className="results-score-large">
            <div className="score-circle-large" style={{ 
              background: `conic-gradient(var(--accent) ${percentage * 3.6}deg, var(--surface) 0deg)` 
            }}>
              <div className="score-inner">
                <span className="score-big">{percentage}%</span>
                <span className="score-fraction">{score}/{results?.total ?? questions.length}</span>
              </div>
            </div>
          </div>
          
          <div className="results-verdict">
            {passed ? (
              <>
                <span className="verdict-pass">✅ APROBADO</span>
                <p>Has alcanzado el porcentaje mínimo requerido</p>
              </>
            ) : (
              <>
                <span className="verdict-fail">❌ NO APROBADO</span>
                <p>Necesitas al menos 60% para aprobar</p>
              </>
            )}
          </div>
        </header>
        
        <main className="simulador-main-results">
          <div className="results-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-value">{formatTime(results?.timeSpent || 0)}</div>
              <div className="stat-label">Tiempo empleado</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-value">{percentage}%</div>
              <div className="stat-label">Precisión global</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-value">{results?.scoreIcne ?? '—'}</div>
              <div className="stat-label">Puntaje Ceneval (ICNE){results?.nivelIcne ? ` · ${results.nivelIcne}` : ''}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-value">{score}</div>
              <div className="stat-label">Respuestas correctas</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-value">{(results?.total ?? questions.length) - score}</div>
              <div className="stat-label">Respuestas incorrectas</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏭️</div>
              <div className="stat-value">{Object.keys(answers).filter(k => answers[k] === undefined).length}</div>
              <div className="stat-label">Sin responder</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-value">{config.areas.length}</div>
              <div className="stat-label">Áreas evaluadas</div>
            </div>
          </div>
          
          {/* Desglose por área */}
          <section className="results-breakdown">
            <h3>Desglose por Área</h3>
            <div className="breakdown-list">
              {config.areas.map(areaId => {
                const areaQuestions = results?.details.filter(d => d.areaId === areaId) || [];
                const areaCorrect = areaQuestions.filter(d => d.isCorrect).length;
                const areaTotal = areaQuestions.length;
                const areaPercentage = areaTotal > 0 ? Math.round((areaCorrect / areaTotal) * 100) : 0;
                
                return (
                  <div key={areaId} className="breakdown-item">
                    <div className="breakdown-info">
                      <span className="breakdown-area">{allAreas.find(a => a.id === areaId)?.nombre || `Área ${areaId}`}</span>
                      <span className="breakdown-count">{areaCorrect}/{areaTotal}</span>
                    </div>
                    <div className="breakdown-bar">
                      <div 
                        className="breakdown-fill" 
                        style={{ width: `${areaPercentage}%`, background: areaPercentage >= 60 ? 'var(--accent)' : '#f44336' }}
                      ></div>
                    </div>
                    <span className="breakdown-percent">{areaPercentage}%</span>
                  </div>
                );
              })}
            </div>
          </section>
          
          {/* Desglose por tema */}
          <section className="results-breakdown">
            <h3>Desglose por Tema</h3>
            <div className="breakdown-list">
              {Object.entries(results?.details.reduce((acc, d) => {
                const key = `${d.temaId}-${d.temaNombre}`;
                if (!acc[key]) acc[key] = { correct: 0, total: 0 };
                acc[key].total++;
                if (d.isCorrect) acc[key].correct++;
                return acc;
              }, {})).map(([key, data]) => {
                const [temaId, temaNombre] = key.split('-', 2);
                const percentage = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
                
                return (
                  <div key={key} className="breakdown-item">
                    <div className="breakdown-info">
                      <span className="breakdown-area">{temaNombre}</span>
                      <span className="breakdown-count">{data.correct}/{data.total}</span>
                    </div>
                    <div className="breakdown-bar">
                      <div 
                        className="breakdown-fill" 
                        style={{ width: `${percentage}%`, background: percentage >= 60 ? 'var(--accent)' : '#f44336' }}
                      ></div>
                    </div>
                    <span className="breakdown-percent">{percentage}%</span>
                  </div>
                );
              })}
            </div>
          </section>
          
          <div className="results-actions">
            {step === 'results' && (
              <button onClick={handleReview} className="btn-secondary btn-large">
                📝 Revisar respuestas detalladas
              </button>
            )}
            <button onClick={handleRetry} className="btn-primary btn-large">
              🔄 Repetir simulador
            </button>
            <button onClick={() => navigate('/dashboard')} className="btn-outline btn-large">
              🏠 Volver al inicio
            </button>
          </div>
          
          {/* Vista de revisión detallada */}
          {step === 'review' && (
            <section className="review-section">
              <h3>Revisión Detallada</h3>
              <div className="review-list">
                {results?.details.map((detail, idx) => (
                  <div key={detail.questionId} className={`review-item ${detail.isCorrect ? 'correct' : detail.userAnswer !== undefined ? 'incorrect' : 'unanswered'}`}>
                    <div className="review-header">
                      <span className="review-number">P{idx + 1}</span>
                      <span className="review-area">{detail.areaNombre}</span>
                      <span className="review-tema">{detail.temaNombre}</span>
                      <span className={`review-status ${detail.isCorrect ? 'correct' : detail.userAnswer !== undefined ? 'incorrect' : 'unanswered'}`}>
                        {detail.isCorrect ? '✓ Correcta' : detail.userAnswer !== undefined ? '✗ Incorrecta' : '⏭ Sin responder'}
                      </span>
                    </div>
                    <div className="review-question">
                      <QuestionText text={detail.question} />
                    </div>
                    {detail.grafica && (
                      <div className="review-graph" style={{ margin: '0.75rem 0' }}>
                        <MathGraph config={detail.grafica} />
                      </div>
                    )}
                    <div className="review-answers">
                      {getRespuestas({ respuestas: detail.respuestas || {}, opciones: detail.opciones }).map((r, ri) => (
                        <div key={ri} className={`review-answer ${ri === detail.correctAnswer ? 'correct-answer' : ''} ${ri === detail.userAnswer && !detail.isCorrect ? 'user-answer' : ''}`}>
                          <span>{String.fromCharCode(65 + ri)}</span>
                          <span className="review-answer-text"><OptionText text={r} /></span>
                          {ri === detail.correctAnswer && <span className="badge">✓ Correcta</span>}
                          {ri === detail.userAnswer && !detail.isCorrect && <span className="badge user">Tu respuesta</span>}
                        </div>
                      ))}
                    </div>
                    {detail.explicacion && (
                      <div className="review-explanation">💡 <QuestionText text={detail.explicacion} /></div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    );
  }

  return null;
};

// Helper para iconos de área
function getAreaIcon(areaId) {
  const icons = {
    1: '📐', 2: '📖', 3: '✍️', 4: '🌐', 5: '💼', 6: '🔢',
    7: '🧬', 8: '📈', 9: '🩺', 10: '⚖️', 11: '📊', 12: '💡',
    13: '⚡', 14: '🏛️', 15: '📚', 16: '💰', 17: '⚕️', 18: '🎲',
    19: '🧠', 20: '🧪', 21: '🔬', 22: '👥'
  };
  return icons[areaId] || '📘';
}

export default SimuladorExamen;